const ESHOP_ID = '3250846';
const CALLBACK_URL = 'https://cvs-callback.max-wang.workers.dev';
const MAP_URL =
  'https://emap.presco.com.tw/c2cemap.ashx' +
  `?eshopid=${ESHOP_ID}&servicetype=1&url=${encodeURIComponent(CALLBACK_URL)}`;
const CALLBACK_ORIGIN = new URL(CALLBACK_URL).origin;
const STORAGE_KEY = 'understand_cvs_store';
const CHANGE_EVENT = 'cvs:change';
const STORE_PARAMS = ['storeid', 'storename', 'storeaddress', 'telephone', 'storetype'];

/** @type {string | null} */
let syncedStoreId = null;

/**
 * @param {any} raw
 * @returns {{ storeid: string, storename: string, storeaddress: string, telephone: string, storetype: string } | null}
 */
function sanitize(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const clean = (/** @type {any} */ value, /** @type {number} */ max) => String(value ?? '').slice(0, max);
  const storeid = clean(raw.storeid, 32).trim();
  if (!storeid) return null;
  return {
    storeid,
    storename: clean(raw.storename, 100),
    storeaddress: clean(raw.storeaddress, 200),
    telephone: clean(raw.telephone, 32),
    storetype: clean(raw.storetype, 16),
  };
}

function loadSavedStore() {
  try {
    return sanitize(JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null'));
  } catch {
    return null;
  }
}

/** @param {NonNullable<ReturnType<typeof sanitize>>} store */
function saveStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    // localStorage が使えない環境ではカート属性だけを正とする
  }
}

/** @param {NonNullable<ReturnType<typeof sanitize>>} store */
async function syncAttributes(store) {
  if (syncedStoreId === store.storeid) return;
  syncedStoreId = store.storeid;
  try {
    const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        attributes: {
          cvs_storeid: store.storeid,
          cvs_storename: store.storename,
          cvs_storeaddress: store.storeaddress,
        },
      }),
    });
    if (!response.ok) syncedStoreId = null;
  } catch {
    syncedStoreId = null;
  }
}

let currentStore = loadSavedStore();

/** @param {any} raw */
function selectStore(raw) {
  const store = sanitize(raw);
  if (!store) return;
  currentStore = store;
  saveStore(store);
  syncAttributes(store);
  document.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: store }));
}

/** @param {string} popupMessage */
function openMap(popupMessage) {
  const popup = window.open(MAP_URL, 'cvs_map', 'width=900,height=680,scrollbars=yes,resizable=yes');
  if (!popup) alert(popupMessage);
}

window.addEventListener('message', (event) => {
  if (event.data?.type !== 'cvs_store_selected') return;
  if (event.origin !== CALLBACK_ORIGIN && event.origin !== window.location.origin) return;
  selectStore(event.data);
});

const url = new URL(window.location.href);
if (url.searchParams.has('storeid')) {
  selectStore(Object.fromEntries(STORE_PARAMS.map((key) => [key, url.searchParams.get(key)])));
  STORE_PARAMS.forEach((key) => url.searchParams.delete(key));
  history.replaceState({}, '', url.pathname + url.search + url.hash);
}

class CvsPickup extends HTMLElement {
  /** @type {HTMLButtonElement[]} */
  #methodButtons = [];
  /** @type {HTMLElement | null} */
  #panel = null;
  /** @type {HTMLElement | null} */
  #storeBox = null;
  /** @type {HTMLElement | null} */
  #selectButton = null;

  connectedCallback() {
    this.#methodButtons = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-method]')]);
    this.#panel = this.querySelector('.cvs-pickup__panel');
    this.#storeBox = this.querySelector('.cvs-pickup__store');
    this.#selectButton = this.querySelector('[data-action="select"]');

    this.addEventListener('click', this.#handleClick);
    document.addEventListener(CHANGE_EVENT, this.#handleChange);

    if (currentStore) {
      this.#showStore(currentStore);
      this.#setMethod('cvs');
      if (this.dataset.storeId === currentStore.storeid) {
        syncedStoreId ??= currentStore.storeid;
      } else {
        syncAttributes(currentStore);
      }
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    document.removeEventListener(CHANGE_EVENT, this.#handleChange);
  }

  /** @param {Event} event */
  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const methodButton = /** @type {HTMLElement | null | undefined} */ (target?.closest('[data-method]'));
    if (methodButton) {
      const method = methodButton.dataset.method ?? 'delivery';
      this.#setMethod(method);
      if (method === 'cvs' && !currentStore) openMap(this.dataset.textPopup ?? '');
      return;
    }
    if (target?.closest('[data-action="select"]')) openMap(this.dataset.textPopup ?? '');
  };

  /** @param {Event} event */
  #handleChange = (event) => {
    this.#showStore(/** @type {CustomEvent} */ (event).detail);
    this.#setMethod('cvs');
  };

  /** @param {string} method */
  #setMethod(method) {
    this.#methodButtons.forEach((button) => {
      const active = button.dataset.method === method;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
    if (this.#panel) this.#panel.hidden = method !== 'cvs';
  }

  /** @param {{ storeid: string, storename: string, storeaddress: string }} store */
  #showStore(store) {
    const name = this.querySelector('[data-ref="name"]');
    const address = this.querySelector('[data-ref="address"]');
    if (name) name.textContent = store.storename || store.storeid;
    if (address) address.textContent = store.storeaddress;
    if (this.#storeBox) this.#storeBox.hidden = false;
    if (this.#selectButton) this.#selectButton.textContent = this.dataset.textChange ?? '';
  }
}

if (!customElements.get('cvs-pickup')) customElements.define('cvs-pickup', CvsPickup);
